/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import { of, throwError } from "rxjs";
import { ApplicationError } from "../../../../models/ui/application-error.model";
import {
	GET_CLOUD_FILTERS_RESPONSE,
	GET_CLOUD_PERFORMANCE_RESPONSE,
	GET_CLOUD_TOTAL_EMISSION_FOR_PIE_CHART_RESPONSE,
	GET_EMISSIONS_ANALYSIS_BY_SCOPE_PERCENTAGE_RESPONSE,
	GET_EMISSIONS_ANALYSIS_BY_SCOPE_VALUE_RESPONSE,
	GET_EXPORT_CSV_RESPONSE,
	GET_EXPORT_REPORT_RESPONSE,
	GET_REGION_EMISSIONS_ANALYSIS_BY_SCOPE_PERCENTAGE_RESPONSE,
	GET_REGION_EMISSIONS_ANALYSIS_BY_SCOPE_VALUE_RESPONSE,
	GET_TOTAL_CO2_RESPONSE,
	GET_WORST_10_APPLICATIONS_RESPONSE,
	GET_WORST_10_LOCATIONS_RESPONSE
} from "./dashboard-cloud.db";

export class DashboardCloud {
	public static getCloudFilters(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;
		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getCloudFilters.name
					)
			);
		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getCloudFilters.name
					)
			);

		return of(GET_CLOUD_FILTERS_RESPONSE);
	}

	public static getCloudPerformance(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;
		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getCloudPerformance.name
					)
			);
		const tenantId = httpParams.get("tenantId");
		const cloudProvider = httpParams.get("cloudProviders");
		const applicationId = httpParams.get("applications");
		const locationId = httpParams.get("locations");
		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");

		if (!tenantId || !cloudProvider || !applicationId || !locationId || !startDate || !endDate)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getCloudPerformance.name
					)
			);

		return of(GET_CLOUD_PERFORMANCE_RESPONSE);
	}

	public static getCloudEmissionsForPieChart(options: HttpRequestOptions) {
		const { params } = options;
		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getCloudPerformance.name
					)
			);
		const tenantId = httpParams.get("tenantId");
		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");
		const cloudProvider = httpParams.get("cloudProviders");
		const applicationId = httpParams.get("applications");
		const locationId = httpParams.get("locations");

		if (!tenantId || !cloudProvider || !applicationId || !locationId || !startDate || !endDate)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getCloudPerformance.name
					)
			);

		return of(GET_CLOUD_TOTAL_EMISSION_FOR_PIE_CHART_RESPONSE);
	}

	public static getTotalCo2(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getTotalCo2.name
					)
			);
		const tenantId = httpParams.get("tenantId");
		const applicationId = httpParams.get("applications");
		const locationId = httpParams.get("locations");
		const cloudProvider = httpParams.get("cloudProviders");
		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");

		if (!startDate || !endDate || !applicationId || !tenantId || !locationId || !cloudProvider)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getTotalCo2.name
					)
			);

		return of(GET_TOTAL_CO2_RESPONSE);
	}

	public static getWorst10Applications(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getWorst10Applications.name
					)
			);
		const tenantId = httpParams.get("tenantId");
		const cloudProvider = httpParams.get("cloudProviders");
		const applicationId = httpParams.get("applications");
		const locationId = httpParams.get("locations");
		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");

		if (!startDate || !endDate || !applicationId || !tenantId || !locationId || !cloudProvider)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getWorst10Applications.name
					)
			);
		return of(GET_WORST_10_APPLICATIONS_RESPONSE);
	}

	public static getWorst10Locations(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getWorst10Locations.name
					)
			);
		const tenantId = httpParams.get("tenantId");
		const cloudProvider = httpParams.get("cloudProviders");
		const applicationId = httpParams.get("applications");
		const locationId = httpParams.get("locations");
		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");

		if (!startDate || !endDate || !applicationId || !tenantId || !locationId || !cloudProvider)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getWorst10Locations.name
					)
			);
		return of(GET_WORST_10_LOCATIONS_RESPONSE);
	}

	public static getEmissionsAnalysis(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getEmissionsAnalysis.name
					)
			);
		const tenantId = httpParams.get("tenantId");
		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");
		const mode = httpParams.get("mode");
		const orderBy = httpParams.get("orderBy");

		if (!startDate || !endDate || !tenantId || !mode || !orderBy)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getEmissionsAnalysis.name
					)
			);
		return of(
			mode === "PERCENTAGE"
				? GET_EMISSIONS_ANALYSIS_BY_SCOPE_PERCENTAGE_RESPONSE
				: GET_EMISSIONS_ANALYSIS_BY_SCOPE_VALUE_RESPONSE
		);
	}

	public static getRegionsEmissions(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;
		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getRegionsEmissions.name
					)
			);
		const tenantId = httpParams.get("tenantId");
		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");
		const cloudProviders = httpParams.get("cloudProviders");
		const locations = httpParams.get("locations");
		const applications = httpParams.get("applications");
		const mode = httpParams.get("mode");
		const orderBy = httpParams.get("orderBy");
		if (
			!startDate ||
			!endDate ||
			!tenantId ||
			!cloudProviders ||
			!locations ||
			!applications ||
			!mode ||
			!orderBy
		)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getRegionsEmissions.name
					)
			);
		return of(
			mode === "PERCENTAGE"
				? GET_REGION_EMISSIONS_ANALYSIS_BY_SCOPE_PERCENTAGE_RESPONSE
				: GET_REGION_EMISSIONS_ANALYSIS_BY_SCOPE_VALUE_RESPONSE
		);
	}

	public static exportCsv(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.exportCsv.name
					)
			);

		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");
		const cloudProviders = httpParams.get("cloudProviders");
		const locations = httpParams.get("locations");
		const applications = httpParams.get("applications");
		const tenantId = httpParams.get("tenantId");

		if (!startDate || !endDate || !applications || !cloudProviders || !tenantId || !locations)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.exportCsv.name
					)
			);

		return of(GET_EXPORT_CSV_RESPONSE);
	}

	public static exportReport(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.exportReport.name
					)
			);

		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");
		const cloudProviders = httpParams.get("cloudProviders");
		const locations = httpParams.get("locations");
		const applications = httpParams.get("applications");
		const tenantId = httpParams.get("tenantId");

		if (!startDate || !endDate || !applications || !cloudProviders || !tenantId || !locations)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.exportReport.name
					)
			);

		return of(GET_EXPORT_REPORT_RESPONSE);
	}
}
